/**
 * DialerModal — dial a custom number with the desktop web softphone.
 *
 * Opened from the side menu's New Call button and the call-history page. The in-call UI is the
 * app-wide FloatingCallWidget (rendered by VoiceCallProvider), so this closes itself as soon as a
 * call starts and only owns the dial affordance. A technician only gets the numbers technicians can
 * use (the server filters the list), possibly none.
 */

'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GripVertical, PhoneOff, X } from 'lucide-react';
import { apiService } from '@/lib/api';
import { voiceManager } from '@/lib/voiceManager';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useModalLayer } from '@/lib/layerManager';
import { formatVoipNumberLabel } from '@/lib/phoneUtils';
import { DialPad } from './DialPad';
import type { PhoneNumberDTO } from '@/types';

interface DialerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DialerModal({ isOpen, onClose }: DialerModalProps) {
  const { callState } = useVoiceCall();
  const { userRole } = useCurrentUser();
  const isTechnician = userRole === 'technician';
  // No back-navigation entry: the app stays usable while the panel is open, so closing it must not
  // pop the user back from a page they navigated to in the meantime.
  const layer = useModalLayer({ enabled: isOpen, onClose, backNavigation: false });
  const [supported, setSupported] = useState<boolean | null>(null);
  const [voiceNumbers, setVoiceNumbers] = useState<PhoneNumberDTO[] | null>(null);
  const [callerId, setCallerId] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setVoiceNumbers(null);
    (async () => {
      const ok = await voiceManager.isSupported().catch(() => false);
      if (cancelled) return;
      setSupported(ok);
      if (!ok) return;
      // Pre-warm the softphone so the first call fires the invite without a cold login.
      void voiceManager.init().catch(() => { /* errors surface at call time */ });
      const [nums, provider] = await Promise.all([
        apiService.getPhoneNumbers().catch(() => [] as PhoneNumberDTO[]),
        voiceManager.getActiveProvider().catch(() => 'telnyx' as const),
      ]);
      if (cancelled) return;
      const voice = nums.filter((n) => n.capabilitiesVoice && n.isActive);
      setVoiceNumbers(voice);
      // Caller-ID must match the active softphone provider (a Telnyx call dials from a Telnyx number).
      const preferred = voice.find((n) => n.provider.toLowerCase() === provider) ?? voice[0];
      setCallerId(preferred?.e164Number ?? '');
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  // Once the call is under way the FloatingCallWidget owns it — get the dialer out of the way.
  useEffect(() => {
    if (isOpen && callState !== 'idle') onClose();
  }, [isOpen, callState, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') layer.onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, layer]);

  // Keep the panel mounted through its slide-out, and start it off-screen so it slides in.
  const [mounted, setMounted] = useState(isOpen);
  const [slidIn, setSlidIn] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      const raf = requestAnimationFrame(() => requestAnimationFrame(() => setSlidIn(true)));
      return () => cancelAnimationFrame(raf);
    }
    setSlidIn(false);
    const t = setTimeout(() => setMounted(false), 300);
    return () => clearTimeout(t);
  }, [isOpen]);

  // Drag offset from the panel's resting spot (right edge, vertically centred); reset on each open.
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ px: number; py: number; x: number; y: number; rect: DOMRect } | null>(null);
  useEffect(() => {
    if (isOpen) setOffset({ x: 0, y: 0 });
  }, [isOpen]);

  const onDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return;
    const panel = e.currentTarget.closest('[role="dialog"]');
    if (!panel) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { px: e.clientX, py: e.clientY, x: offset.x, y: offset.y, rect: panel.getBoundingClientRect() };
  };
  const onDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    // Keep the whole panel on screen.
    const dx = Math.min(Math.max(e.clientX - d.px, -d.rect.left), window.innerWidth - d.rect.right);
    const dy = Math.min(Math.max(e.clientY - d.py, -d.rect.top), window.innerHeight - d.rect.bottom);
    setOffset({ x: d.x + dx, y: d.y + dy });
  };
  const onDragEnd = () => { dragRef.current = null; };

  if (!mounted) return null;

  const noNumbers = voiceNumbers !== null && voiceNumbers.length === 0;
  const moved = offset.x !== 0;

  // Side panel with no backdrop: the rest of the app stays clickable while the dialer is open.
  return createPortal(
    <div className="pointer-events-none fixed inset-0" style={layer.style}>
      <div
        className="absolute right-0 top-1/2 w-[calc(100vw-1rem)] max-w-sm"
        style={{ transform: `translate(${offset.x}px, calc(-50% + ${offset.y}px))` }}
      >
      <div
        role="dialog"
        aria-label="Dialer"
        className={`pointer-events-auto flex max-h-[calc(100dvh-2rem)] flex-col overflow-y-auto bg-gradient-to-b from-gray-900 to-gray-800 shadow-[-12px_0_32px_rgba(0,0,0,0.35)] ring-1 ring-white/10 transition-transform duration-300 ease-out ${
          moved ? 'rounded-3xl' : 'rounded-l-3xl'
        } ${slidIn ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
          className="flex cursor-grab touch-none select-none items-center justify-between border-b border-white/10 px-5 py-3 active:cursor-grabbing"
        >
          <div className="flex items-center gap-2">
            <GripVertical className="h-4 w-4 text-gray-500" aria-hidden />
            <p className="text-sm font-semibold text-white">New Call</p>
          </div>
          <button onClick={layer.onClose} className="rounded-full p-1 text-gray-400 hover:bg-white/10 hover:text-white" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {supported === false || noNumbers ? (
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <PhoneOff className="h-8 w-8 text-gray-500" />
            <p className="text-sm font-medium text-white">Dialer unavailable</p>
            <p className="text-xs text-gray-400">
              {isTechnician
                ? 'No company number is available to you.'
                : 'The web dialer requires a voice number configured for your company.'}
            </p>
          </div>
        ) : (
          <div className="px-7 py-6">
            {/* Caller-ID picker — shows the number's nickname alongside the formatted number. */}
            <label className="mb-5 block">
              <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-gray-400">Call from</span>
              <select
                value={callerId}
                onChange={(e) => setCallerId(e.target.value)}
                className="w-full rounded-lg border border-white/10 bg-white/10 px-3 py-2.5 text-sm text-white focus:border-blue-400 focus:outline-none"
              >
                {(voiceNumbers ?? []).map((n) => (
                  <option key={n.e164Number} value={n.e164Number} className="text-black">
                    {formatVoipNumberLabel(n.e164Number, n.displayName)}
                  </option>
                ))}
              </select>
            </label>

            <DialPad callerIdE164={callerId} />
          </div>
        )}
      </div>
      </div>
    </div>,
    document.body,
  );
}
